import { Avatar, Tooltip, cn } from '@orgus/ui';
import { useTeams, useViewers } from './model.ts';
import { usePresence } from './usePresence.ts';

/** Overlapping avatars for a few people, "+N" for the rest. */
export function AvatarStack({
  people,
  max = 3,
  size = 20,
  className,
  ringClassName = 'ring-surface',
}: {
  people: { login: string; name: string }[];
  max?: number;
  size?: number;
  className?: string;
  /** The ring that parts overlapping faces: the colour of the surface they sit on. */
  ringClassName?: string;
}) {
  const shown = people.slice(0, max);
  const more = people.length - shown.length;
  return (
    <span className={cn('flex items-center -space-x-1', className)} aria-hidden>
      {shown.map((p) => (
        <span key={p.login} className={cn('flex rounded-full ring-2', ringClassName)}>
          <Avatar name={p.name} colorKey={p.login} size={size} />
        </span>
      ))}
      {more > 0 && (
        <span className={cn('flex items-center justify-center rounded-full bg-hover px-1 text-caption font-medium text-fg-muted ring-2 num', ringClassName)} style={{ height: size, minWidth: size }}>
          +{more}
        </span>
      )}
    </span>
  );
}

function sentence(names: string[]): string {
  if (names.length <= 1) return names.join('');
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

/**
 * Who else is looking at this session, flow, mission or pipeline, live. Rendering it also
 * tells the hub you are here (heartbeats while the tab is visible). Shows nothing when
 * you are alone, so it never adds noise.
 */
export function PresenceAvatars({ view, className }: { view: string; className?: string }) {
  usePresence(view);
  const { status } = useTeams();
  const viewers = useViewers(view);
  if (status !== 'ready' || !viewers.length) return null;
  const names = viewers.map((v) => v.name);
  const label = `Also looking at this: ${sentence(names)}`;
  const first = names[0]!.split(' ')[0];
  // One quiet unit, centred with the header's buttons: the faces, then "Maya is here".
  return (
    <Tooltip content={`${sentence(names)} ${names.length === 1 ? 'is' : 'are'} looking at this too.`}>
      <span tabIndex={0} aria-label={label} role="status" className={cn('flex h-control-sm shrink-0 items-center gap-2 rounded-control px-1 text-caption text-fg-subtle', className)}>
        <AvatarStack people={viewers} ringClassName="ring-popover" />
        <span className="hidden whitespace-nowrap sm:inline">{viewers.length === 1 ? `${first} is here` : `${viewers.length} here`}</span>
      </span>
    </Tooltip>
  );
}
